import { useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { useFormik } from "formik";
import { noop } from "lodash";

import SettingsForm from "../Forms/SettingsForm";
import Restricted from "features/Application/Restricted";

import { bindAction } from "interfaces/redux";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type IWizardStep } from "interfaces/IWizardStepsManager";

import { RolePermissions } from "enums";
import { normalizedTagsSelector, saveSettings } from "../state/ExternalLinkDetailsSlice";
import { externalLinkSettings } from "utils/validationSchemas/externalLinksValidationSchema";
import { fetchExternalLinkTags } from "../state/thunks/externalLinkQueriesThunk";
import { updateTagsOnExternalLink } from "../state/thunks/externalLinkCommandThunk";
import { useEventCallback } from "hooks/useEventCallback";

export type SettingsTabProps = IWizardStep & PropsFromRedux;

export const SettingsTab = (props: SettingsTabProps) => {
  const { settings, normalizedTags, isLoading, acceptHandlers, entityId, disabled } = props;

  useEffect(() => {
    acceptHandlers?.({
      onPrevious: saveTags,
      onNext: saveTags,
    });
    props.fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tagsFormik = useFormik({
    initialValues: settings,
    validationSchema: externalLinkSettings,
    onSubmit: noop,
    enableReinitialize: true,
  });

  const saveTags = useEventCallback(() => {
    props.saveSettings(tagsFormik.values);
  }, [props, tagsFormik.values]);

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission) => (
        <SettingsForm
          entityId={entityId}
          tagsFormik={tagsFormik}
          normalizedTags={normalizedTags}
          updateTags={props.updateTags}
          settings={settings}
          isDisabled={disabled || !hasPermission}
          isLoading={isLoading}
        />
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = state.library.externalLinks;

  return {
    entityId: base.entityState.entityId,
    settings: base.details.link.settings,
    normalizedTags: normalizedTagsSelector(state),
    isLoading: base.details.isLoading,
    disabled: base.details.link?.isPurchased || false, // Add any specific disabled logic if needed
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchAll: bindAction(fetchExternalLinkTags, dispatch),
  saveSettings: bindAction(saveSettings, dispatch),
  updateTags: bindAction(updateTagsOnExternalLink, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SettingsTab);
export default ConnectedComponent;
